import React, { useCallback, useRef, useState } from 'react';
import { useResolvedMediaUrl } from '../hooks/useResolvedMediaUrl';
import { resolveMediaUrl } from '../utils/mediaApi';

export type SignedVideoProps = Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'src'> & {
  src: string;
};

const SignedVideo = React.forwardRef<HTMLVideoElement, SignedVideoProps>(({ src, poster, ...rest }, ref) => {
  const [refreshToken, setRefreshToken] = useState(0);
  const lastRefreshAtRef = useRef(0);
  const resolved = useResolvedMediaUrl(src, refreshToken);
  const resolvedPoster = useResolvedMediaUrl(poster || '');

  const handleError = useCallback(async () => {
    // Browser doesn't expose HTTP status on media element errors.
    // Best effort: when a media/* key is in use, force a fresh presign and retry.
    if (!src || !src.startsWith('media/')) return;
    const now = Date.now();
    if (now - lastRefreshAtRef.current < 10_000) return; // throttle
    lastRefreshAtRef.current = now;

    try {
      await resolveMediaUrl(src, { bypassCache: true });
    } catch {
      // ignore
    }
    setRefreshToken((x) => x + 1);
  }, [src]);

  const chainedOnError = useCallback<React.ReactEventHandler<HTMLVideoElement>>(
    (e) => {
      try {
        const orig = (rest as any).onError as React.ReactEventHandler<HTMLVideoElement> | undefined;
        orig?.(e);
      } finally {
        handleError();
      }
    },
    [handleError, rest]
  );

  return <video ref={ref} {...rest} poster={resolvedPoster || undefined} src={resolved || ''} onError={chainedOnError} />;
});

SignedVideo.displayName = 'SignedVideo';

export default SignedVideo;
